import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';

/**
 * Sends Phil a message in the client's continuous chat thread.
 * Used by the unlock request flow and the suggestion boxes.
 */
export function useClientRequest() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [sending, setSending] = useState(false);

  const sendRequest = async (messageText: string): Promise<boolean> => {
    if (!user || !messageText.trim()) return false;
    setSending(true);

    try {
      let threadId: string | undefined;
      const { data: existing } = await supabase
        .from('client_threads')
        .select('id')
        .eq('client_id', user.id)
        .limit(1);

      threadId = existing?.[0]?.id;

      if (!threadId) {
        const { data: created, error: createErr } = await supabase
          .from('client_threads')
          .insert({ client_id: user.id, status: 'active', title: 'Conversation with Phil' } as any)
          .select('id')
          .single();
        if (createErr) throw createErr;
        threadId = created?.id;
      }

      if (!threadId) throw new Error('No conversation thread available');

      const { error } = await supabase.from('client_messages').insert({
        thread_id: threadId,
        sender_role: 'client',
        sender_id: user.id,
        message_text: messageText.trim(),
      } as any);

      if (error) throw error;

      toast({
        title: 'Sent to Phil',
        description: 'He will get back to you in your chat.',
      });
      return true;
    } catch (err: any) {
      toast({
        title: "Couldn't send that",
        description: err?.message ?? 'Please try again in a moment.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSending(false);
    }
  };

  return { sendRequest, sending };
}
